import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Flex, Form, Input, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyContactInfoModal from '@/components/general/CompanyContactInfoModal';
import LinkTextField from '@/components/general/LinkTextField';
import { COMPANY_URLS } from '@/constants/urls';
import { useUserContext } from '@/contexts/UserContext';
import Company from '@/model/General/Company';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ShipmentShowPaymentProps {
  record?: Shipment;
  packageItems?: PackageItem[];
  refetch?: () => Promise<any>;
}

const ShipmentShowPayment = (props: ShipmentShowPaymentProps) => {
  const { t } = useTranslation();
  const { record, packageItems, refetch } = props;
  const { userWorkArea } = useUserContext();
  const [openModal, setOpenModal] = useState({ sales_order: false, billing_number: false });
  const closeModal = () => {
    setOpenModal({ sales_order: false, billing_number: false });
  };

  const billingNumbers = useMemo(() => {
    if (!packageItems) return [];

    const billingNumbers: string[] = [];

    packageItems.forEach(item => {
      if (item.billing_number && !billingNumbers.includes(item.billing_number)) {
        billingNumbers.push(item.billing_number);
      }
    });

    return billingNumbers;
  }, [packageItems]);

  return (
    <div className="ShipmentShowDataCard">
      <div className="ShipmentShowData">
        <b>{t('Razón social')}</b>
        <Flex gap={4}>
          <LinkTextField
            label={
              record?.shipment_payment_company
                ? `${record?.shipment_payment_company?.full_name} (${record?.shipment_payment_company?.id})`
                : '-'
            }
            to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record?.shipment_payment_company?.id}`}
            admin
          />
          {record?.shipment_payment_company && (
            <CompanyContactInfoModal
              company={new Company(record?.shipment_payment_company)}
              openButton={
                <Button
                  className="ShipmentShowInfoButton"
                  type="text"
                  size={'small'}
                  icon={<InfoCircleOutlined />}
                />
              }
            />
          )}
        </Flex>
      </div>
      <div className="ShipmentShowData">
        <b>{'CUIT'}</b>
        <TextField value={record?.shipment_payment_cuit} />
      </div>
      {record?.has_sale_items && (
        <div className="ShipmentShowData">
          <Flex gap={6}>
            <b>{t('Orden de venta')}</b>
            {userWorkArea == USER_WORK_AREA.LOGISTICS &&
              record?.status == PACKAGE_STATES.PENDIENTE && (
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => setOpenModal({ ...openModal, sales_order: true })}
                />
              )}
          </Flex>
          <TextField
            value={
              record?.external_document == undefined || record?.external_document == ''
                ? '-'
                : record?.external_document
            }
          />
        </div>
      )}
      {(record?.has_sale_items || billingNumbers.length > 0) && (
        <div className="ShipmentShowData">
          <Flex gap={6}>
            <b>{billingNumbers.length > 1 ? t('Números de factura') : t('Número de factura')}</b>
            {userWorkArea == USER_WORK_AREA.ADMINISTRATION &&
              record?.status == PACKAGE_STATES.GESTION_PAGO && (
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => setOpenModal({ ...openModal, billing_number: true })}
                />
              )}
          </Flex>
          {billingNumbers.length == 0 ? (
            '-'
          ) : (
            <ul style={{ paddingInlineStart: 20 }}>
              {billingNumbers.map(num => (
                <li key={num}>{num}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {(record?.status == PACKAGE_STATES.PENDIENTE ||
        record?.status == PACKAGE_STATES.GESTION_PAGO) && (
        <Modal
          open={openModal.sales_order || openModal.billing_number}
          onCancel={() => closeModal()}
          footer={null}
          title={
            openModal.sales_order ? t('Editar orden de venta') : t('Editar número de factura')
          }>
          {openModal.sales_order ? (
            <UpdateSalesOrder record={record} closeModal={closeModal} refetch={refetch} />
          ) : (
            <UpdateBillingNumber
              record={record}
              closeModal={closeModal}
              packageItems={packageItems}
              refetch={refetch}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

interface UpdateSalesOrderProps {
  record?: Shipment;
  closeModal: () => void;
  refetch?: () => Promise<any>;
}

const UpdateSalesOrder = (props: UpdateSalesOrderProps) => {
  const { t } = useTranslation();
  const { record, closeModal, refetch } = props;
  const { shipmentRepository } = useRepositoriesContext();
  const [externalDocument, setExternalDocument] = useState<string | undefined>(
    record?.external_document,
  );
  const onConfirmChange = useCallback(() => {
    if (!record) {
      return;
    } else if (externalDocument != undefined) {
      void shipmentRepository
        .updateShipmentExternalDocument(record.id, externalDocument)
        .then(() => void refetch?.().then(() => closeModal()));
    }
  }, [closeModal, externalDocument, record, refetch, shipmentRepository]);

  return (
    <Form onFinish={onConfirmChange} layout="vertical">
      <Form.Item>
        <Input
          defaultValue={record?.external_document}
          value={externalDocument}
          onChange={e => setExternalDocument(e.target.value)}
        />
      </Form.Item>
      <Flex justify="end" gap={8}>
        <Button onClick={closeModal}>{t('Cancelar')}</Button>
        <Button type="primary" htmlType="submit">
          {t('Guardar')}
        </Button>
      </Flex>
    </Form>
  );
};

interface UpdateBillingOrderProps {
  record?: Shipment;
  packageItems?: PackageItem[];
  refetch?: () => Promise<any>;
  closeModal: () => void;
}

const UpdateBillingNumber = (props: UpdateBillingOrderProps) => {
  const { t } = useTranslation();
  const { record, packageItems, closeModal, refetch } = props;
  const { packageItemRepository } = useRepositoriesContext();
  const [billingNumber, setBillingNumber] = useState<string | undefined>();

  useEffect(() => {
    if (packageItems) {
      const sellItemsBillingNumbers: string[] = [];

      packageItems.forEach(item => {
        if (item.billing_number && !sellItemsBillingNumbers.includes(item.billing_number)) {
          sellItemsBillingNumbers.push(item.billing_number);
        }
      });

      setBillingNumber(sellItemsBillingNumbers.join(', '));
    }
  }, [packageItems]);
  const onConfirmChange = useCallback(() => {
    if (!record) {
      return;
    } else if (billingNumber != undefined) {
      const packageToUpdate =
        packageItems?.filter(
          item => item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO,
        ) || [];

      packageToUpdate.forEach(packageItem => {
        packageItem.billing_number = billingNumber;
        packageItem.toUpdate = true;
      });

      void packageItemRepository
        .savePackageItems(packageToUpdate, record.id)
        .then(() => void refetch?.().then(() => closeModal()));
    }
  }, [record, billingNumber, packageItems, packageItemRepository, refetch, closeModal]);

  return (
    <Form onFinish={onConfirmChange} layout="vertical">
      <Form.Item>
        <Input
          defaultValue={record?.external_document}
          value={billingNumber}
          onChange={e => setBillingNumber(e.target.value)}
        />
      </Form.Item>
      <Flex justify="end" gap={8}>
        <Button onClick={closeModal}>{t('Cancelar')}</Button>
        <Button type="primary" htmlType="submit">
          {t('Guardar')}
        </Button>
      </Flex>
    </Form>
  );
};

export default ShipmentShowPayment;
